import React, { useCallback, useEffect, useState } from "react";
import { FiVideo } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import VideoProviderSelector from "./VideoProviderSelector";
import ZoomAuthSection from "./AuthSection/ZoomAuthSection";
import GoogleAuthSection from "./AuthSection/GoogleAuthSection";
import MeetingSection from "./MeetingSection";

import { getProvider } from "./Providers";
import ZoomServices from "../../services/ZoomServices";
import GoogleMeetServices from "../../services/GoogleMeetService";

import "../../assets/css/VideoConference.css";

export default function VideoConference() {
  const navigate = useNavigate();

  const [provider, setProvider] = useState("zoom");
  const [connectedMap, setConnectedMap] = useState({
    zoom: false,
    google: false,
  });

  const refreshConnection = useCallback(async (providerKey) => {
    try {
      const token = await getProvider(providerKey).getValidAccessToken();
      setConnectedMap((prev) => ({ ...prev, [providerKey]: !!token }));
      return token;
    } catch {
      setConnectedMap((prev) => ({ ...prev, [providerKey]: false }));
      return null;
    }
  }, []);

  useEffect(() => {
    const appToken = localStorage.getItem("accessToken");
    if (!appToken) {
      navigate("/signin");
      return;
    }

    (async () => {
      for (const key of ["zoom", "google"]) {
        const token = await refreshConnection(key);
        if (token) {
          try {
            await getProvider(key).deleteExpiredMeetings(token);
          } catch (err) {
            console.error(`Failed to delete expired ${key} meetings`, err);
          }
        }
      }
    })();

    const handler = async (e) => {
      if (e.data?.type === "ZOOM_OAUTH_SUCCESS") {
        const token = await refreshConnection("zoom");

        if (token) {
          try {
            await getProvider("zoom").deleteExpiredMeetings(token);
          } catch (err) {
            console.error("Failed to delete expired Zoom meetings", err);
          }
        }
      }

      if (e.data?.type === "GOOGLE_OAUTH_SUCCESS") {
        const token = await refreshConnection("google");

        if (token) {
          try {
            await getProvider("google").deleteExpiredMeetings();
          } catch (err) {
            console.error("Failed to delete expired Google Meet meetings", err);
          }
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigate, refreshConnection]);

  const isSelectedConnected = connectedMap[provider];

  const disconnectZoom = async () => {
    try {
      await ZoomServices.disconnect();
    } finally {
      setConnectedMap((prev) => ({ ...prev, zoom: false }));
    }
  };

  const disconnectGoogle = async () => {
    try {
      await GoogleMeetServices.disconnect();
    } finally {
      setConnectedMap((prev) => ({ ...prev, google: false }));
    }
  };

  return (
    <div className="video-conference-wrapper">
      <h5 className="video-conference-title page-title">
        <FiVideo /> Video Conference
      </h5>
      <p className="page-subtitle">
        Schedule and manage meetings across multiple platforms
      </p>

      <VideoProviderSelector provider={provider} setProvider={setProvider} />

      <ZoomAuthSection connected={connectedMap.zoom} onDisconnect={disconnectZoom} />
      <GoogleAuthSection connected={connectedMap.google} onDisconnect={disconnectGoogle} />

      {isSelectedConnected && <MeetingSection providerKey={provider} />}
    </div>
  );
}
